/**
 * Live call monitoring — every call live on the account right now.
 *
 * Operations serves it at `/api/voice/live-calls` (snapshot) and
 * `/api/voice/live-calls/stream` (SSE: a `snapshot`, then `call` changes),
 * proxying the calling service, which keeps one row per customer call. Access is
 * `calls:view` at ALL scope. Transcripts come from the existing per-call
 * transcript stream, which labels speakers when the monitor is on.
 *
 * The list state is a pure reducer so the stream, the polling fallback and the
 * tests all agree on what a snapshot and a change mean.
 */

import { apiGet, apiPut, operationsClient } from '@/lib/api';

// ─── Access ───────────────────────────────────────────────────────────────────

/** The grant the operations API checks: `calls:view` held at ALL scope. */
export const LIVE_CALLS_PERMISSION = 'calls:view';

export function canMonitorLiveCalls(
  permissions: readonly string[] | undefined,
  permissionScopes: Record<string, string> | undefined,
): boolean {
  return (
    !!permissions?.includes(LIVE_CALLS_PERMISSION) &&
    permissionScopes?.[LIVE_CALLS_PERMISSION] === 'ALL'
  );
}

// ─── Wire types ───────────────────────────────────────────────────────────────

export type LiveCallStatus =
  | 'ringing'
  | 'ivr'
  | 'ai'
  | 'dialing'
  | 'connected'
  | 'on_hold'
  | 'transferring'
  | 'voicemail'
  | 'ending';

export type LiveTranscriptState = 'off' | 'pending' | 'starting' | 'live' | 'failed';

export interface LiveCallPerson {
  userId: string | null;
  name: string | null;
  since?: number | null;
  until?: number | null;
}

export interface LiveCall {
  id: string;
  direction: 'inbound' | 'outbound';
  status: LiveCallStatus;
  statusChangedAt: number;
  companyNumber: string | null;
  companyNumberLabel: string | null;
  customerNumber: string | null;
  contactId: string | null;
  contactName: string | null;
  agent: LiveCallPerson | null;
  agentHistory: LiveCallPerson[];
  transferTarget: LiveCallPerson | null;
  transferCount: number;
  aiHandled: boolean;
  onHold: boolean;
  transcript: { state: LiveTranscriptState; reason: string | null };
  startedAt: number;
  answeredAt: number | null;
  endedAt?: number | null;
  version: number;
}

export interface LiveCallNumber {
  number: string;
  label: string | null;
}

export interface LiveCallsSnapshot {
  serverTime: number;
  calls: LiveCall[];
  numbers: LiveCallNumber[];
  people: { userId: string; name: string | null }[];
}

export interface LiveCallChange {
  type: 'call.updated' | 'call.ended' | 'call.removed';
  call: LiveCall;
  serverTime?: number;
}

// ─── State ────────────────────────────────────────────────────────────────────

export interface LiveCallsState {
  /** Live calls by id. */
  calls: Record<string, LiveCall>;
  /** Calls that just ended, kept briefly so the row can say so before leaving. */
  ending: Record<string, { call: LiveCall; endedAt: number }>;
  numbers: LiveCallNumber[];
  /** serverTime - Date.now() at the last message, so timers don't drift with a skewed clock. */
  clockOffsetMs: number;
  loaded: boolean;
}

export const initialLiveCallsState: LiveCallsState = {
  calls: {},
  ending: {},
  numbers: [],
  clockOffsetMs: 0,
  loaded: false,
};

/** How long an ended call stays on screen, faded, before it leaves the list. */
export const ENDED_LINGER_MS = 4_000;

export type LiveCallsAction =
  | { type: 'snapshot'; snapshot: LiveCallsSnapshot; now: number }
  | { type: 'change'; change: LiveCallChange; now: number }
  | { type: 'prune'; now: number }
  | { type: 'reset' };

export function liveCallsReducer(state: LiveCallsState, action: LiveCallsAction): LiveCallsState {
  switch (action.type) {
    case 'reset':
      return initialLiveCallsState;

    case 'snapshot': {
      const calls: Record<string, LiveCall> = {};
      for (const call of action.snapshot.calls ?? []) calls[call.id] = call;
      // Anything we were showing as ending that the server still lists is live
      // again (an out-of-order view); anything else keeps fading out.
      const ending = Object.fromEntries(Object.entries(state.ending).filter(([id]) => !calls[id]));
      return {
        calls,
        ending,
        numbers: action.snapshot.numbers ?? state.numbers,
        clockOffsetMs: offsetFrom(action.snapshot.serverTime, action.now, state.clockOffsetMs),
        loaded: true,
      };
    }

    case 'change': {
      const { call, type, serverTime } = action.change;
      if (!call?.id) return state;
      const clockOffsetMs = offsetFrom(serverTime, action.now, state.clockOffsetMs);
      const known = state.calls[call.id];
      // Versions only move forward; a stale change is a no-op.
      if (known && call.version <= known.version) return state;

      if (type === 'call.ended' || type === 'call.removed') {
        const { [call.id]: gone, ...rest } = state.calls;
        if (!gone && type === 'call.removed') return { ...state, clockOffsetMs };
        return {
          ...state,
          calls: rest,
          ending:
            type === 'call.ended'
              ? { ...state.ending, [call.id]: { call, endedAt: action.now } }
              : state.ending,
          clockOffsetMs,
        };
      }
      return { ...state, calls: { ...state.calls, [call.id]: call }, clockOffsetMs };
    }

    case 'prune': {
      const ending = Object.fromEntries(
        Object.entries(state.ending).filter(([, e]) => action.now - e.endedAt < ENDED_LINGER_MS),
      );
      return Object.keys(ending).length === Object.keys(state.ending).length
        ? state
        : { ...state, ending };
    }

    default:
      return state;
  }
}

function offsetFrom(serverTime: number | undefined, now: number, previous: number): number {
  return typeof serverTime === 'number' && Number.isFinite(serverTime)
    ? serverTime - now
    : previous;
}

// ─── Presentation helpers ─────────────────────────────────────────────────────

export const LIVE_STATUS_LABELS: Record<LiveCallStatus | 'ended', string> = {
  ringing: 'Ringing',
  ivr: 'In menu',
  ai: 'With AI receptionist',
  dialing: 'Dialing',
  connected: 'Connected',
  on_hold: 'On hold',
  transferring: 'Transferring',
  voicemail: 'Voicemail',
  ending: 'Ending',
  ended: 'Ended',
};

export type LiveStatusTone = 'ringing' | 'active' | 'hold' | 'transfer' | 'automated' | 'ending';

export function liveStatusTone(status: LiveCallStatus | 'ended'): LiveStatusTone {
  switch (status) {
    case 'ringing':
    case 'dialing':
      return 'ringing';
    case 'connected':
      return 'active';
    case 'on_hold':
      return 'hold';
    case 'transferring':
      return 'transfer';
    case 'ivr':
    case 'ai':
    case 'voicemail':
      return 'automated';
    default:
      return 'ending';
  }
}

export const TRANSCRIPT_STATE_MESSAGES: Record<LiveTranscriptState, string> = {
  off: 'Live transcription is turned off for this account.',
  pending: 'The transcript starts when the call is answered.',
  starting: 'Waiting for someone to speak…',
  live: '',
  failed: 'Live transcription could not start for this call.',
};

/** "12:05" or "1:02:05" from milliseconds. */
export function formatCallDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** "(555) 000-1111" for NANP numbers; anything else as given. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length === 10) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return value;
}

/** Digits without the NANP country code, so two spellings of one number match. */
export function phoneKey(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/** Who the row names as handling the call. */
export function handlerLabel(call: LiveCall): string {
  if (call.status === 'ai') return 'AI receptionist';
  if (call.status === 'ivr') return 'Phone menu';
  if (call.status === 'voicemail') return 'Voicemail';
  if (call.agent?.name) return call.agent.name;
  if (call.status === 'ringing') return 'Ringing team';
  return call.agent?.userId ? 'Team member' : 'Unassigned';
}

// ─── API ──────────────────────────────────────────────────────────────────────

const BASE = '/api/voice/live-calls';

export interface LiveCallsFilters {
  number?: string;
  direction?: 'inbound' | 'outbound';
  agentUserId?: string;
  status?: string;
}

export const liveCallsApi = {
  list: (filters: LiveCallsFilters = {}) =>
    apiGet<LiveCallsSnapshot & { success: boolean }>(operationsClient, BASE, { params: filters }),

  /** The softphone reporting hold/resume (or just "this call is mine"). */
  reportAgentState: (callControlId: string, body: { onHold?: boolean; clientRef?: string } = {}) =>
    apiPut<unknown>(
      operationsClient,
      `${BASE}/${encodeURIComponent(callControlId)}/agent-state`,
      body,
    ),

  streamUrl: () => `${BASE}/stream`,
};
